// ===========================================
// BUSTED! BLACKJACK - CONNECTION WARNING
// ===========================================
// Animated WiFi icon displayed over the player's seat
// during brief network disconnections (0-5 seconds).
//
// After 5 seconds, the full ReconnectingModal takes over.
// This tiered approach:
// - Alerts player immediately without full-screen blocking
// - Gives them a chance to check their connection
// - Escalates to modal only for extended disconnects
// ===========================================

import { useEffect, useState, useRef } from 'react';

// Import WiFi animation frames
import wifi1 from '../../assets/wifi/1.png';
import wifi2 from '../../assets/wifi/2.png';
import wifi3 from '../../assets/wifi/3.png';
import wifi4 from '../../assets/wifi/4.png';

const WIFI_FRAMES = [wifi1, wifi2, wifi3, wifi4];
const FRAME_DURATION_MS = 250; // 250ms per frame = 1 second full loop

// ===========================================
// PRELOAD IMAGES ON MODULE LOAD
// ===========================================
// Critical: These images must be cached BEFORE going offline,
// otherwise they can't be fetched when we need to show them.
// This runs once when the module is imported.
const preloadedImages: HTMLImageElement[] = [];
WIFI_FRAMES.forEach((src, i) => {
  const img = new Image();
  img.src = src;
  img.onload = () => console.log(`[ConnectionWarning] Preloaded wifi frame ${i + 1}`);
  img.onerror = () => console.error(`[ConnectionWarning] Failed to preload wifi frame ${i + 1}`);
  preloadedImages.push(img);
});

interface ConnectionWarningProps {
  /** Whether to show the warning */
  show: boolean;
  /** Callback when 5-second threshold is reached */
  onEscalate?: () => void;
}

const ESCALATION_SECONDS = 5;

export function ConnectionWarning({ show, onEscalate }: ConnectionWarningProps) {
  const [frameIndex, setFrameIndex] = useState(0);
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const hasEscalatedRef = useRef(false);
  const onEscalateRef = useRef(onEscalate);

  // Keep ref up to date without causing effect re-runs
  useEffect(() => {
    onEscalateRef.current = onEscalate;
  }, [onEscalate]);

  // Reset when warning shows/hides
  useEffect(() => {
    if (show) {
      setFrameIndex(0);
      setSecondsElapsed(0);
      hasEscalatedRef.current = false;
    }
  }, [show]);

  // Animate through frames
  useEffect(() => {
    if (!show) return;

    const timer = setInterval(() => {
      setFrameIndex((prev) => (prev + 1) % WIFI_FRAMES.length);
    }, FRAME_DURATION_MS);

    return () => clearInterval(timer);
  }, [show]);

  // Track seconds elapsed
  useEffect(() => {
    if (!show) return;

    const timer = setInterval(() => {
      setSecondsElapsed((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [show]);

  // Escalate after threshold (separate effect to avoid calling setState during setState)
  useEffect(() => {
    if (secondsElapsed >= ESCALATION_SECONDS && !hasEscalatedRef.current && onEscalateRef.current) {
      hasEscalatedRef.current = true;
      onEscalateRef.current();
    }
  }, [secondsElapsed]);

  if (!show) return null;

  return (
    <img
      src={WIFI_FRAMES[frameIndex]}
      alt="Connection lost"
      className="w-full h-full object-cover drop-shadow-lg opacity-75"
    />
  );
}

export default ConnectionWarning;
